import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  Landmark,
  ShieldCheck,
  BadgePercent,
  Handshake,
  Building2,
  ArrowLeft
} from 'lucide-react';
import TopStatsNav from '../components/TopStatsNav';

export default function Investment() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#f8fafc] font-[IRANSansWeb,Tahoma] p-6 md:p-10" dir="rtl">
      <div className="max-w-6xl mx-auto space-y-8">
        <header className="bg-white rounded-[2.5rem] border border-gray-100 shadow-sm p-8 md:p-10 overflow-hidden relative">
          <div className="absolute inset-0 opacity-40 bg-[radial-gradient(circle_at_top,rgba(250,204,21,0.12),transparent_70%)]" />
          <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div className="text-right">
              <div className="inline-flex items-center gap-2 bg-amber-50 text-amber-700 px-4 py-2 rounded-2xl text-[11px] font-extrabold border border-amber-100">
                <Landmark size={16} />
                <span>فرصت‌ها و پروژه‌های سرمایه‌گذاری کریدور</span>
              </div>

              <h1 className="mt-6 text-3xl md:text-4xl font-black text-gray-900 leading-tight">
                سرمایه‌گذاری
              </h1>

              <p className="mt-4 text-sm text-gray-500 leading-8">
                این صفحه برای مرور فرصت‌ها، آشنایی با مدل‌های مشارکت و مشوق‌ها، و ثبت علاقه‌مندی سرمایه‌گذاری در پروژه‌های
                لجستیکی و زیرساختی طراحی شده است.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={() => navigate('/dashboard')}
                  className="h-12 px-5 rounded-2xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 font-bold text-sm transition inline-flex items-center justify-center gap-2"
                >
                  <ArrowRight size={18} />
                  بازگشت به داشبورد
                </button>

                <button
                  type="button"
                  onClick={() => navigate('/trade')}
                  className="h-12 px-5 rounded-2xl bg-[#a16207] hover:bg-[#854d0e] text-white font-bold text-sm transition inline-flex items-center justify-center gap-2 shadow-lg shadow-amber-900/15"
                >
                  رفتن به تجارت
                  <ArrowLeft size={18} />
                </button>
              </div>
            </div>

            <div className="bg-[#fbfdff] border border-gray-100 rounded-[2.5rem] p-6 shadow-sm flex items-center justify-center overflow-hidden">
              <img
                src="/invs.png"
                alt="تصویر سرمایه‌گذاری"
                className="w-full h-auto max-h-[320px] object-contain rounded-[1.75rem] shadow-lg shadow-slate-900/10 ring-1 ring-slate-900/5"
                loading="lazy"
              />
            </div>
          </div>
        </header>

        <TopStatsNav
          items={[
            {
              label: 'خدمات تجارت فرامرزی',
              value: 14,
              unit: 'درخواست آماده بررسی',
              route: '/trade',
              accent: 'blue',
              sparkValues: [2, 4, 5, 5, 7, 8]
            },
            {
              label: 'حمل‌ونقل و رهگیری چندوجهی',
              value: 9,
              unit: 'عملیات در جریان',
              route: '/transport',
              accent: 'emerald',
              sparkValues: [3, 4, 6, 6, 7, 9]
            }
          ]}
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <section className="lg:col-span-2 bg-white rounded-[2.5rem] border border-gray-100 shadow-sm p-8">
            <div className="flex items-start justify-between gap-6">
              <div className="text-right">
                <h2 className="text-lg font-black text-gray-800">چه چیزهایی اینجا می‌بینید؟</h2>
                <p className="text-[11px] text-gray-400 mt-2 leading-6">
                  اطلاعات خلاصه برای تصمیم‌گیری سریع، و مسیرهای پیشنهادی برای ورود به پروژه‌ها.
                </p>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-amber-50 border border-amber-100 flex items-center justify-center text-amber-700">
                <Building2 size={22} />
              </div>
            </div>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Feature
                icon={<ShieldCheck size={18} className="text-amber-700" />}
                title="مرور پروژه‌ها و فرصت‌ها"
                desc="مشاهده خلاصه پروژه‌ها، وضعیت مجوز، پیشرفت، و سطح ریسک."
              />
              <Feature
                icon={<BadgePercent size={18} className="text-amber-700" />}
                title="مشوق‌ها و مجوزها (پیشنهادی)"
                desc="مسیرهای اخذ مجوز، الزامات و مدل‌های مشارکت قابل ارائه."
              />
              <Feature
                icon={<Handshake size={18} className="text-amber-700" />}
                title="ثبت علاقه‌مندی"
                desc="ثبت درخواست جلسه، معرفی سرمایه‌گذار و پیگیری مرحله‌ای."
              />
              <Feature
                icon={<Landmark size={18} className="text-amber-700" />}
                title="مدل‌های مشارکت"
                desc="مقایسه مدل‌های سرمایه‌گذاری (BOT/BOO/PPP) به‌صورت خلاصه."
              />
            </div>
          </section>

          <aside className="bg-white rounded-[2.5rem] border border-gray-100 shadow-sm p-8">
            <div className="text-right">
              <h3 className="text-lg font-black text-gray-800">نمونه معیارهای ارزیابی</h3>
              <p className="text-[11px] text-gray-400 mt-2 leading-6">برای مقایسه سریع فرصت‌ها.</p>
            </div>

            <div className="mt-8 space-y-3">
              <Metric label="بازده مورد انتظار" value="متوسط تا بالا" />
              <Metric label="افق زمانی" value="میان‌مدت / بلندمدت" />
              <Metric label="سطح ریسک" value="قابل مدیریت" />
              <Metric label="وضعیت مجوز" value="در حال اخذ / تکمیل" />
            </div>
          </aside>
        </div>

        <section className="bg-[#1A3062] rounded-[2.5rem] p-8 md:p-10 text-white shadow-xl overflow-hidden relative">
          <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.18),transparent_70%)]" />
          <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <h3 className="text-xl font-black">می‌خواهید پروژه‌ها را با نیاز تجاری خود هم‌راستا کنید؟</h3>
              <p className="text-[11px] text-blue-200 mt-3 leading-6">
                ابتدا پرونده تجاری را ثبت کنید و سپس از بخش سرمایه‌گذاری برای پیشنهادهای زیرساختی متناسب استفاده کنید.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={() => navigate('/trade')}
                className="h-12 px-6 rounded-2xl bg-yellow-400 hover:bg-yellow-500 text-[#1A3062] font-black transition inline-flex items-center gap-2 shadow-lg"
              >
                رفتن به تجارت
                <ArrowLeft size={18} />
              </button>
              <button
                type="button"
                onClick={() => navigate('/transport')}
                className="h-12 px-6 rounded-2xl bg-white/10 hover:bg-white/20 border border-white/20 text-white font-black transition inline-flex items-center gap-2"
              >
                رفتن به حمل‌ونقل
                <ArrowLeft size={18} />
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function Feature({ icon, title, desc }) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-[#fbfdff] p-5 hover:shadow-sm transition">
      <div className="flex items-center gap-2">
        <div className="w-10 h-10 rounded-2xl bg-amber-50 border border-amber-100 flex items-center justify-center">
          {icon}
        </div>
        <div className="text-sm font-extrabold text-gray-800">{title}</div>
      </div>
      <div className="text-[11px] text-gray-400 mt-2 leading-6">{desc}</div>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-4 p-4 rounded-2xl border border-gray-100 bg-[#fbfdff]">
      <div className="text-[11px] text-gray-400 font-bold">{label}</div>
      <div className="text-[12px] text-gray-800 font-extrabold">{value}</div>
    </div>
  );
}

